// Playback timeline (§4.11 step 1): unroll repeats, voltas and D.C./D.S. into a
// list of played bars, apply the tempo map and triplet feel, merge ties, extend
// let-ring notes and give grace notes their time. Used by the audio scheduler,
// the playhead, the visualizers and MIDI export.

import { PPQ, WHOLE, ticks as durTicks, midiOf, barCapacity, beatGroups } from '@simplenotation/theory';
import type { Score, Beat, Note, BendPoint, Dynamic, BarInfo } from './model/types';
import { timeSigAt, filledTicks } from './model/query';

export interface PlayedBar {
  /** Index into Score.bars. */
  barIdx: number;
  /** Played tick at which this bar starts. */
  startTick: number;
  /** Length in ticks (capacity, or actual content length for pickups/over-full bars). */
  length: number;
  /** Repeat pass (1-based) this bar is played in. */
  pass: number;
}

export interface NoteEvent {
  track: number;
  voice: number;
  barIdx: number;
  /** Index into Timeline.bars. */
  playedBar: number;
  beatId: string;
  noteId: string;
  startTick: number;
  durTicks: number;
  /** Sounding MIDI number. */
  midi: number;
  /** 0…1 */
  velocity: number;
  string?: number;
  fret?: number;
  dead?: boolean;
  palmMute?: boolean;
  harmonic?: 'natural' | 'artificial';
  bend?: BendPoint[];
  vibrato?: boolean;
  /** Legato slide/hammer into the next note (no re-attack in a perfect world; informative). */
  legato?: boolean;
  grace?: boolean;
}

export interface BeatSpan {
  track: number;
  voice: number;
  barIdx: number;
  playedBar: number;
  beatIdx: number;
  beatId: string;
  startTick: number;
  durTicks: number;
  rest: boolean;
}

export interface MetronomeClick {
  tick: number;
  accent: boolean;
}

export interface TempoPoint {
  tick: number;
  /** Quarter notes per minute. */
  qpm: number;
  /** Seconds elapsed at this tick (speed 1). */
  sec: number;
}

export interface Timeline {
  bars: PlayedBar[];
  totalTicks: number;
  events: NoteEvent[];
  beats: BeatSpan[];
  clicks: MetronomeClick[];
  tempo: TempoPoint[];
}

export interface TimelineOptions {
  /** Follow repeats and jumps (default true). */
  repeats?: boolean;
}

const MAX_PLAYED_BARS = 20000;

/** Content length of a bar across all tracks/voices (grace notes take no time). */
export function barContentTicks(score: Score, barIdx: number): number {
  let max = 0;
  for (const t of score.tracks) {
    const tb = t.bars[barIdx];
    if (!tb) continue;
    for (const v of tb.voices) max = Math.max(max, filledTicks(v));
  }
  return max;
}

export function playedBarLength(score: Score, barIdx: number): number {
  const b = score.bars[barIdx];
  const cap = barCapacity(timeSigAt(score, barIdx)) * (b.multiRest && b.multiRest > 1 ? b.multiRest : 1);
  const content = barContentTicks(score, barIdx);
  if (b.pickup) return content > 0 ? content : cap;
  return Math.max(cap, content);
}

/** Unroll repeats, voltas and D.C./D.S. jumps into played order. */
export function unrollBars(score: Score, opts: TimelineOptions = {}): PlayedBar[] {
  const bars = score.bars;
  const n = bars.length;
  const follow = opts.repeats !== false;
  const out: PlayedBar[] = [];
  let tick = 0;
  const emit = (i: number, pass: number) => {
    const length = playedBarLength(score, i);
    out.push({ barIdx: i, startTick: tick, length, pass });
    tick += length;
  };
  if (!follow) {
    for (let i = 0; i < n; i++) emit(i, 1);
    return out;
  }
  const segno = bars.findIndex((b) => b.direction === 'Segno');
  const coda = bars.findIndex((b) => b.direction === 'Coda');
  const counts = new Map<number, number>();
  let repeatStart = 0;
  let pass = 1;
  let jumped = false;
  let i = 0;
  while (i < n && out.length < MAX_PLAYED_BARS) {
    const b: BarInfo = bars[i];
    if (b.repeatStart && !jumped && (counts.get(i) ?? 0) === 0) {
      // Entering a repeat section through normal flow.
      if (repeatStart !== i) pass = 1;
      repeatStart = i;
    }
    if (b.endings && b.endings.length && !b.endings.includes(pass)) {
      i++;
      continue;
    }
    emit(i, pass);
    if (jumped && b.direction === 'Fine') break;
    if (jumped && b.direction === 'ToCoda' && coda >= 0) {
      i = coda;
      continue;
    }
    if (b.repeatEnd && b.repeatEnd > 1 && !jumped) {
      const c = counts.get(i) ?? 1;
      if (c < b.repeatEnd) {
        counts.set(i, c + 1);
        pass = c + 1;
        i = repeatStart;
        continue;
      }
      counts.set(i, 1);
      pass = 1;
      // After an exhausted repeat, a later repeat section starts fresh.
      repeatStart = i + 1;
    }
    if (!jumped && b.direction && /^D[CS]/.test(b.direction)) {
      jumped = true;
      pass = 1;
      i = b.direction.startsWith('DS') && segno >= 0 ? segno : 0;
      continue;
    }
    i++;
  }
  return out;
}

const DYN_VELOCITY: Record<Dynamic, number> = {
  ppp: 0.22,
  pp: 0.32,
  p: 0.42,
  mp: 0.52,
  mf: 0.62,
  f: 0.75,
  ff: 0.87,
  fff: 0.97,
  fp: 0.75,
  fz: 0.85,
  sf: 0.85,
  sfz: 0.9,
  sffz: 0.95,
};

/** Tempo (quarter notes per minute) of a tempo mark. */
export function markQpm(m: { bpm: number; beatUnit: number; dotted?: boolean }): number {
  const unitTicks = (WHOLE / m.beatUnit) * (m.dotted ? 1.5 : 1);
  return (m.bpm * unitTicks) / PPQ;
}

/** Swing warp of a tick position within a bar. */
function swing(pos: number, feel: 'none' | '8th' | '16th' | undefined): number {
  if (!feel || feel === 'none') return pos;
  const unit = feel === '8th' ? PPQ : PPQ / 2;
  const half = unit / 2;
  const base = Math.floor(pos / unit) * unit;
  const p = pos - base;
  const long = (unit * 2) / 3;
  const warped = p < half ? (p * long) / half : long + ((p - half) * (unit - long)) / half;
  return base + warped;
}

function feelAt(score: Score, barIdx: number): 'none' | '8th' | '16th' {
  for (let i = barIdx; i >= 0; i--) {
    const f = score.bars[i].tripletFeel;
    if (f) return f;
  }
  return 'none';
}

export function buildTimeline(score: Score, opts: TimelineOptions = {}): Timeline {
  const bars = unrollBars(score, opts);
  const totalTicks = bars.length ? bars[bars.length - 1].startTick + bars[bars.length - 1].length : 0;

  // ---- tempo map ----
  const marks = [...score.tempos].sort((a, b) => a.bar - b.bar || a.beatOffsetTicks - b.beatOffsetTicks);
  const tempoPts: { tick: number; qpm: number }[] = [];
  const qpmAtStartOf = (barIdx: number): number => {
    let q = marks.length ? markQpm(marks[0]) : 120;
    for (const m of marks) {
      if (m.bar < barIdx || (m.bar === barIdx && m.beatOffsetTicks === 0)) q = markQpm(m);
      else break;
    }
    return q;
  };
  for (const pb of bars) {
    tempoPts.push({ tick: pb.startTick, qpm: qpmAtStartOf(pb.barIdx) });
    for (const m of marks) {
      if (m.bar === pb.barIdx && m.beatOffsetTicks > 0 && m.beatOffsetTicks < pb.length) {
        tempoPts.push({ tick: pb.startTick + m.beatOffsetTicks, qpm: markQpm(m) });
      }
    }
  }
  if (tempoPts.length === 0) tempoPts.push({ tick: 0, qpm: marks.length ? markQpm(marks[0]) : 120 });
  const tempo: TempoPoint[] = [];
  let sec = 0;
  for (let i = 0; i < tempoPts.length; i++) {
    const p = tempoPts[i];
    if (i > 0) {
      const prev = tempo[tempo.length - 1];
      sec = prev.sec + ((p.tick - prev.tick) / PPQ) * (60 / prev.qpm);
      if (prev.qpm === p.qpm) continue;
    }
    tempo.push({ tick: p.tick, qpm: p.qpm, sec });
  }

  // ---- metronome ----
  const clicks: MetronomeClick[] = [];
  for (const pb of bars) {
    const ts = timeSigAt(score, pb.barIdx);
    const cap = barCapacity(ts);
    const starts: number[] = [];
    let acc = 0;
    for (const g of beatGroups(ts)) {
      starts.push(acc);
      acc += g;
    }
    // Pickup bars count the last beats of a full bar.
    const pickup = !!score.bars[pb.barIdx].pickup;
    const offset = pickup ? pb.length - cap : 0;
    for (let rep = 0; rep * cap + offset < pb.length; rep++) {
      for (const s0 of starts) {
        const at = rep * cap + s0 + offset;
        if (at >= 0 && at < pb.length) clicks.push({ tick: pb.startTick + at, accent: s0 === 0 && !pickup });
      }
    }
  }

  // ---- events ----
  const events: NoteEvent[] = [];
  const beatsOut: BeatSpan[] = [];
  score.tracks.forEach((track, ti) => {
    const maxVoices = Math.max(1, ...track.bars.map((tb) => tb.voices.length));
    for (let vi = 0; vi < maxVoices; vi++) {
      let dyn = 0.62;
      const openTies = new Map<number, NoteEvent>();
      const voiceEvents: NoteEvent[] = [];
      bars.forEach((pb, pbi) => {
        const beats: Beat[] = track.bars[pb.barIdx]?.voices[vi] ?? [];
        if (vi > 0 && beats.length === 0) return;
        const feel = feelAt(score, pb.barIdx);
        let pos = 0;
        let pendingGrace: Beat[] = [];
        beats.forEach((beat, bi) => {
          if (beat.grace) {
            pendingGrace.push(beat);
            return;
          }
          const len = durTicks(beat.duration);
          const s = pb.startTick + swing(pos, feel);
          const e = pb.startTick + swing(pos + len, feel);
          pos += len;
          beatsOut.push({
            track: ti,
            voice: vi,
            barIdx: pb.barIdx,
            playedBar: pbi,
            beatIdx: bi,
            beatId: beat.id,
            startTick: s,
            durTicks: e - s,
            rest: beat.rest,
          });
          if (beat.dynamics) dyn = DYN_VELOCITY[beat.dynamics];
          let start = s;
          let dur = e - s;
          // Grace notes steal 1/8 of the principal note each (at most half).
          if (pendingGrace.length) {
            const g = Math.min(dur / 8, dur / 2 / pendingGrace.length);
            for (const gb of pendingGrace) {
              for (const n of gb.notes) {
                voiceEvents.push(noteEvent(ti, vi, pb.barIdx, pbi, gb, n, start, g, dyn, track.writtenToSounding, true));
              }
              start += g;
              dur -= g;
            }
            pendingGrace = [];
          }
          if (beat.rest || beat.notes.length === 0) {
            openTies.clear();
            return;
          }
          const arts = beat.articulations ?? [];
          let vel = dyn;
          if (arts.includes('accent')) vel += 0.12;
          if (arts.includes('marcato')) vel += 0.18;
          if (beat.dynamics === 'sfz' || beat.dynamics === 'sf' || beat.dynamics === 'fz' || beat.dynamics === 'sffz') vel += 0.05;
          let sound = dur;
          if (arts.includes('staccato')) sound = dur * 0.5;
          if (arts.includes('staccatissimo')) sound = dur * 0.3;
          if (beat.palmMute) sound = Math.min(sound, dur * 0.6);
          const strum = beat.strum || beat.arpeggio || beat.brush;
          const nextTies = new Map<number, NoteEvent>();
          const ordered = [...beat.notes].sort((a, b) => midiOf(a.pitch) - midiOf(b.pitch));
          if (strum === 'down' || beat.strum === 'down') ordered.reverse();
          ordered.forEach((n, k) => {
            const shift = beat.octaveShift ? (Math.abs(beat.octaveShift) === 15 ? 24 : 12) * Math.sign(beat.octaveShift) : 0;
            const midi = midiOf(n.pitch) + track.writtenToSounding + shift;
            const tied = openTies.get(midi);
            if (tied) {
              tied.durTicks = start + sound - tied.startTick;
              if (n.tieToNext) nextTies.set(midi, tied);
              return;
            }
            const offset = strum ? Math.min(k * (PPQ / 32), dur / 4) : 0;
            const ev = noteEvent(ti, vi, pb.barIdx, pbi, beat, n, start + offset, sound - offset, Math.min(1, vel), track.writtenToSounding, false, shift);
            if (beat.letRing) ev.durTicks = -1; // resolved below
            voiceEvents.push(ev);
            if (n.tieToNext) nextTies.set(midi, ev);
          });
          openTies.clear();
          for (const [k, v] of nextTies) openTies.set(k, v);
        });
      });
      // Let ring: sustain until the next note on the same string (or 4 beats).
      for (let i = 0; i < voiceEvents.length; i++) {
        const ev = voiceEvents[i];
        if (ev.durTicks !== -1) continue;
        let end = ev.startTick + 4 * PPQ;
        for (let j = i + 1; j < voiceEvents.length; j++) {
          const o = voiceEvents[j];
          if (o.startTick <= ev.startTick) continue;
          if ((ev.string !== undefined && o.string === ev.string) || (ev.string === undefined && o.midi === ev.midi)) {
            end = Math.min(end, o.startTick);
            break;
          }
        }
        ev.durTicks = Math.max(1, end - ev.startTick);
      }
      events.push(...voiceEvents);
    }
  });
  events.sort((a, b) => a.startTick - b.startTick || a.track - b.track || a.midi - b.midi);
  beatsOut.sort((a, b) => a.startTick - b.startTick || a.track - b.track || a.voice - b.voice);
  return { bars, totalTicks, events, beats: beatsOut, clicks, tempo };
}

function noteEvent(
  track: number,
  voice: number,
  barIdx: number,
  playedBar: number,
  beat: Beat,
  n: Note,
  start: number,
  dur: number,
  vel: number,
  w2s: number,
  grace: boolean,
  shift = 0,
): NoteEvent {
  const ghost = n.ghost || n.head === 'ghost';
  return {
    track,
    voice,
    barIdx,
    playedBar,
    beatId: beat.id,
    noteId: n.id,
    startTick: start,
    durTicks: Math.max(1, dur),
    midi: midiOf(n.pitch) + w2s + shift,
    velocity: Math.max(0.05, Math.min(1, ghost ? vel * 0.5 : grace ? vel * 0.8 : vel)),
    string: n.string,
    fret: n.fret,
    dead: n.dead || n.head === 'x' || undefined,
    palmMute: beat.palmMute || undefined,
    harmonic: n.harmonic,
    bend: n.bend?.points,
    vibrato: n.vibrato || undefined,
    legato: n.hammerPull || n.slide === 'legato' || undefined,
    grace: grace || undefined,
  };
}

/** Seconds at a played tick (speed factor 1 = written tempo). */
export function tickToSeconds(tl: Pick<Timeline, 'tempo'>, tick: number, speed = 1): number {
  const pts = tl.tempo;
  let lo = 0;
  let hi = pts.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (pts[mid].tick <= tick) lo = mid;
    else hi = mid - 1;
  }
  const p = pts[lo] ?? { tick: 0, qpm: 120, sec: 0 };
  return (p.sec + ((tick - p.tick) / PPQ) * (60 / p.qpm)) / speed;
}

/** Played tick at a time in seconds. */
export function secondsToTick(tl: Pick<Timeline, 'tempo'>, seconds: number, speed = 1): number {
  const s = seconds * speed;
  const pts = tl.tempo;
  let lo = 0;
  let hi = pts.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (pts[mid].sec <= s) lo = mid;
    else hi = mid - 1;
  }
  const p = pts[lo] ?? { tick: 0, qpm: 120, sec: 0 };
  return p.tick + ((s - p.sec) * p.qpm * PPQ) / 60;
}

/** Index of the played bar containing a tick. */
export function playedBarAt(tl: Pick<Timeline, 'bars'>, tick: number): number {
  const bars = tl.bars;
  let lo = 0;
  let hi = bars.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (bars[mid].startTick <= tick) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/** First played tick of a position in the score (bar index + tick within the bar). */
export function tickOfPosition(tl: Pick<Timeline, 'bars'>, barIdx: number, offset = 0, fromPlayedBar = 0): number {
  for (let i = fromPlayedBar; i < tl.bars.length; i++) {
    if (tl.bars[i].barIdx === barIdx) return tl.bars[i].startTick + offset;
  }
  for (let i = 0; i < tl.bars.length; i++) {
    if (tl.bars[i].barIdx >= barIdx) return tl.bars[i].startTick;
  }
  return 0;
}

/** Note events sounding at a tick. */
export function eventsAt(tl: Pick<Timeline, 'events'>, tick: number): NoteEvent[] {
  const out: NoteEvent[] = [];
  for (const e of tl.events) {
    if (e.startTick > tick) break;
    if (e.startTick + e.durTicks > tick) out.push(e);
  }
  return out;
}

export function totalSeconds(tl: Timeline, speed = 1): number {
  return tickToSeconds(tl, tl.totalTicks, speed);
}
